import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { LogOut, BookOpen, Sparkles, Heart, TrendingUp, Crown, Lock, Shield, Printer, Target, Mic, Gem, ClipboardList, Headphones, Video } from 'lucide-react';
import { motion } from 'framer-motion';
import StreakBadge from '../components/StreakBadge';
import StreakCard from '../components/StreakCard';
import TopicsStudiedCard from '../components/TopicsStudiedCard';
import WeaknessNudgeCard from '../components/WeaknessNudgeCard';
import DailyMissionCard from '../components/DailyMissionCard';

const practiceModules = [
  {
    id: 'grammar_cloze',
    name: 'Grammar Cloze',
    description: 'Fill in the blanks with correct grammar',
    color: '#D1FAE5',
    icon: BookOpen
  },
  {
    id: 'vocabulary_cloze',
    name: 'Vocabulary Cloze',
    description: 'Choose the right words',
    color: '#FEF3C7',
    icon: BookOpen
  },
  {
    id: 'grammar_mcq',
    name: 'Grammar MCQ',
    description: 'Multiple choice grammar questions',
    color: '#E0E7FF',
    icon: BookOpen
  },
  {
    id: 'vocabulary_mcq',
    name: 'Vocabulary MCQ',
    description: 'Test your vocabulary knowledge',
    color: '#FFE4E6',
    icon: BookOpen
  },
  {
    id: 'synthesis',
    name: 'Synthesis & Transformation',
    description: 'Transform sentences correctly',
    color: '#F3E8FF',
    icon: BookOpen
  },
  {
    id: 'editing',
    name: 'Editing',
    description: 'Find and fix spelling & grammar errors',
    color: '#DBEAFE',
    icon: BookOpen
  },
  {
    id: 'comprehension_cloze',
    name: 'Comprehension Cloze',
    description: 'Read and complete passages',
    color: '#FCE7F3',
    icon: BookOpen
  },
  {
    id: 'antonyms_synonyms',
    name: 'Antonyms & Synonyms',
    description: 'Words that mean the same or opposite',
    color: '#CCFBF1',
    icon: BookOpen
  },
  {
    id: 'regular_irregular_verbs',
    name: 'Regular & Irregular Verbs',
    description: 'Master past-tense verb forms',
    color: '#FED7AA',
    icon: BookOpen
  }
];

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [subStatus, setSubStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTeacherOrAdmin, setIsTeacherOrAdmin] = useState(false);

  useEffect(() => {
    if (user?.role === 'tutor') {
      navigate('/tutor/dashboard', { replace: true });
      return;
    }
    const fetchStatus = async () => {
      try {
        const data = await api.getSubscriptionStatus();
        setSubStatus(data);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };
    const checkAdmin = async () => {
      try {
        const data = await api.checkAdmin();
        setIsAdmin(!!data.is_admin);
        setIsTeacherOrAdmin(!!data.is_teacher_or_admin);
      } catch (error) {
        setIsAdmin(false);
        setIsTeacherOrAdmin(false);
      }
    };
    fetchStatus();
    checkAdmin();
  }, [user, navigate]);

  const isSubscribedAtUserLevel = subStatus?.subscriptions?.find(
    s => s.level === user?.level
  )?.is_active;

  const getQuotaForModule = (moduleId) => {
    if (!subStatus) return null;
    const q = subStatus.quotas?.find(
      q => q.question_type === moduleId && q.level === user?.level
    );
    return {
      used: q?.used || 0,
      limit: subStatus.free_limit || 50,
      remaining: Math.max(0, (subStatus.free_limit || 50) - (q?.used || 0))
    };
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
                alt="Prof. Scaly"
                className="w-12 h-12 object-contain"
                data-testid="header-mascot-image"
              />
              <div>
                <h1 className="text-2xl font-heading font-bold text-emerald-600" data-testid="header-title">
                  Prof.Scaly
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StreakBadge compact />
              {isTeacherOrAdmin && !isAdmin && (
                <Button
                  onClick={() => navigate('/admin/questions')}
                  variant="outline"
                  size="sm"
                  className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="teacher-questions-button"
                >
                  <BookOpen className="w-4 h-4 mr-1" strokeWidth={2.5} />
                  Questions
                </Button>
              )}
              {isAdmin && (
                <>
                  <Button
                    onClick={() => navigate('/admin')}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="admin-link-button"
                  >
                    <Shield className="w-4 h-4 mr-1" strokeWidth={2.5} />
                    Admin
                  </Button>
                  <Button
                    onClick={() => navigate('/admin/video-generator')}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    data-testid="admin-video-generator-button"
                  >
                    <Video className="w-4 h-4 mr-1" strokeWidth={2.5} />
                    Video Gen
                  </Button>
                </>
              )}
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-slate-700" data-testid="header-user-name">{user?.name}</p>
                <p className="text-xs text-slate-500" data-testid="header-user-level">{user?.level}</p>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-4xl sm:text-5xl font-heading font-bold text-slate-800 mb-2" data-testid="dashboard-welcome">
            Welcome back, {user?.name}! 🎓
          </h2>
          <p className="text-lg text-slate-600" data-testid="dashboard-subtitle">
            Ready to practice for your PSLE exams?
          </p>
        </motion.div>

        {/* Streak Card - Hero */}
        <div className="mb-8 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <StreakCard />
          </div>
          {/* Monster Battle CTA */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02, y: -4 }}
            onClick={() => navigate('/monsters')}
            className="text-left relative overflow-hidden rounded-3xl p-5 border-2 border-orange-400 shadow-sm cursor-pointer bg-gradient-to-br from-orange-500 to-amber-500 text-white"
            data-testid="dashboard-monster-cta"
          >
            <div className="absolute -right-4 -bottom-4 pointer-events-none">
              <img src="/monsters/dirty.jpg" alt="" className="w-32 h-32 object-contain" />
            </div>
            <div className="relative z-10">
              <div className="text-[10px] font-black uppercase tracking-wider text-orange-100 mb-1">
                Game world ...Play &amp; Learn
              </div>
              <div className="text-3xl font-heading font-black mb-1 leading-tight text-emerald-300">
                Fight for Singapore
              </div>
              <p className="text-xs text-orange-50 mb-3 leading-relaxed">
                Every correct answer weakens a monster. Defeat all three to help keep Singapore clean, green &amp; plastic-free.
              </p>
              <div className="inline-flex items-center gap-1 bg-white/25 backdrop-blur px-2.5 py-1 rounded-full text-xs font-bold">
                Enter the battle →
              </div>
            </div>
          </motion.button>
        </div>

        {/* Video Lessons Hero CTA — Prof.Scaly's animated grammar shorts */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
          whileHover={{ scale: 1.005, y: -2 }}
          onClick={() => navigate('/video-lessons')}
          className="w-full text-left mb-8 relative overflow-hidden rounded-3xl p-6 border-2 border-cyan-400 shadow-md cursor-pointer bg-gradient-to-r from-cyan-600 via-sky-500 to-blue-500 text-white"
          data-testid="dashboard-video-lessons-cta"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl">
                <Video className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest mb-1 text-white/80">
                  New · Sora 2 animation
                </div>
                <h3 className="text-2xl font-heading font-black mb-1">Prof.Scaly Video Lessons</h3>
                <p className="text-sm text-white/90 max-w-xl">
                  8-second animated grammar shorts crafted from comic strips — watch, laugh, remember the rule.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:block bg-white/15 backdrop-blur-sm rounded-2xl px-4 py-3">
                <div className="text-[10px] font-black uppercase tracking-wide text-white/70">Format</div>
                <div className="text-sm font-bold">8 s · 720p</div>
              </div>
              <div className="bg-white text-cyan-700 rounded-full px-4 py-2.5 font-black text-sm inline-flex items-center gap-2">
                <Video size={16}/> Watch
              </div>
            </div>
          </div>
        </motion.button>

        {/* Weighted Assessment CTA — P6 only */}
        {user?.level === 'P6' && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.005, y: -2 }}
            onClick={() => navigate('/weighted-assessments')}
            className="w-full text-left mb-8 relative overflow-hidden rounded-3xl p-6 border-2 border-rose-400 shadow-md cursor-pointer bg-gradient-to-r from-rose-600 via-rose-500 to-orange-500 text-white"
            data-testid="dashboard-wa-cta"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest mb-1 text-white/80">
                  Exam simulation · Primary 6
                </div>
                <h3 className="text-2xl font-heading font-black mb-1">Weighted Assessment</h3>
                <p className="text-sm text-white/90 max-w-xl">
                  Full timed P6 English Paper 2 — 90 marks · 1 h 50 min · auto-graded with AI feedback. Practise real exam pressure at home.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="hidden md:block bg-white/15 backdrop-blur-sm rounded-2xl px-4 py-3">
                  <div className="text-[10px] font-black uppercase tracking-wide text-white/70">Sections</div>
                  <div className="text-sm font-bold">A: 25 · B: 65</div>
                </div>
                <div className="bg-white text-rose-700 rounded-full px-4 py-2.5 font-black text-sm inline-flex items-center gap-2">
                  <Target size={16}/> Start
                </div>
              </div>
            </div>
          </motion.button>
        )}

        {/* Diagnostic Package CTA — Tutors & Subscribers */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.005, y: -2 }}
          onClick={() => navigate('/diagnostic')}
          className="w-full text-left mb-8 relative overflow-hidden rounded-3xl p-6 border-2 border-emerald-400 shadow-md cursor-pointer bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-white"
          data-testid="dashboard-diagnostic-cta"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest mb-1 text-white/80">
                Free diagnostic · Primary 6
              </div>
              <h3 className="text-2xl font-heading font-black mb-1">P6 English Diagnostic Test</h3>
              <p className="text-sm text-white/90 max-w-xl">
                Complete 35-question assessment covering all 7 PSLE types · Instant weakness analysis · Unlimited attempts
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:block bg-white/15 backdrop-blur-sm rounded-2xl px-4 py-3">
                <div className="text-[10px] font-black uppercase tracking-wide text-white/70">Questions</div>
                <div className="text-sm font-bold">7 types · 35 Qs</div>
              </div>
              <div className="bg-white text-emerald-700 rounded-full px-4 py-2.5 font-black text-sm inline-flex items-center gap-2">
                <ClipboardList size={16}/> Start
              </div>
            </div>
          </div>
        </motion.button>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-3xl p-6 border-2 border-rose-300 shadow-sm cursor-pointer"
            onClick={() => navigate('/oral-practice')}
            data-testid="oral-practice-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-rose-600 p-3 rounded-2xl">
                <Mic className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider bg-rose-600 text-white px-2 py-0.5 rounded-full">
                New
              </span>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">Oral Reading</h3>
            <p className="text-slate-600 text-sm">Read aloud · AI scores pronunciation</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.085 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-6 border-2 border-purple-300 shadow-sm cursor-pointer"
            onClick={() => window.open('/spelling-pwa/', '_blank')}
            data-testid="spelling-pwa-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-purple-600 p-3 rounded-2xl">
                <Headphones className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider bg-purple-600 text-white px-2 py-0.5 rounded-full">
                New
              </span>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">Spelling Practice</h3>
            <p className="text-slate-600 text-sm">Hear words · camera spelling · offline</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.09 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-6 border-2 border-amber-300 shadow-sm cursor-pointer"
            onClick={() => navigate('/marketplace')}
            data-testid="marketplace-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-amber-600 p-3 rounded-2xl">
                <Gem className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider bg-amber-600 text-white px-2 py-0.5 rounded-full">
                New
              </span>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">Treasure Box</h3>
            <p className="text-slate-600 text-sm">Tutor worksheets · auto-graded</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.095 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-6 border-2 border-cyan-300 shadow-sm cursor-pointer"
            onClick={() => navigate('/video-lessons')}
            data-testid="video-lessons-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-cyan-600 p-3 rounded-2xl">
                <Video className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider bg-cyan-600 text-white px-2 py-0.5 rounded-full">
                New
              </span>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">Video Lessons</h3>
            <p className="text-slate-600 text-sm">Watch grammar lessons · bite-sized</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-white rounded-3xl p-6 border-2 border-slate-200 shadow-sm cursor-pointer"
            onClick={() => navigate('/ai-generate')}
            data-testid="ai-generator-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-amber-100 p-3 rounded-2xl">
                <Sparkles className="w-6 h-6 text-amber-600" strokeWidth={2.5} />
              </div>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">AI Generator</h3>
            <p className="text-slate-600 text-sm">Custom practice questions</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-6 border-2 border-emerald-300 shadow-sm cursor-pointer"
            onClick={() => navigate('/worksheets')}
            data-testid="worksheets-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-emerald-600 p-3 rounded-2xl">
                <Printer className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                New
              </span>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">Printable Worksheets</h3>
            <p className="text-slate-600 text-sm">Print · answer on paper · OCR grades it</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-gradient-to-br from-amber-50 to-emerald-50 rounded-3xl p-6 border-2 border-amber-200 shadow-sm cursor-pointer"
            onClick={() => navigate('/subscription')}
            data-testid="subscription-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-amber-500 p-3 rounded-2xl">
                <Crown className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">Go Premium</h3>
            <p className="text-slate-600 text-sm">Unlimited practice · SGD $2/yr</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-white rounded-3xl p-6 border-2 border-slate-200 shadow-sm cursor-pointer"
            onClick={() => navigate('/favorites')}
            data-testid="favorites-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-pink-100 p-3 rounded-2xl">
                <Heart className="w-6 h-6 text-pink-600" strokeWidth={2.5} />
              </div>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">Favorites</h3>
            <p className="text-slate-600 text-sm">Review saved questions</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className="bg-white rounded-3xl p-6 border-2 border-slate-200 shadow-sm cursor-pointer"
            onClick={() => navigate('/progress')}
            data-testid="progress-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-emerald-100 p-3 rounded-2xl">
                <TrendingUp className="w-6 h-6 text-emerald-600" strokeWidth={2.5} />
              </div>
            </div>
            <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">My Progress</h3>
            <p className="text-slate-600 text-sm">Track your performance</p>
          </motion.div>
        </div>

        {/* Topics I've Studied — coverage of the diversity taxonomy */}
        <div className="mb-8">
          <WeaknessNudgeCard />
          <TopicsStudiedCard />
        </div>

        {/* Daily Scaly Mission */}
        <div className="mb-8">
          <DailyMissionCard />
        </div>

        {/* Practice Modules */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-2xl font-heading font-bold text-slate-800" data-testid="practice-modules-title">
            Practice Modules
          </h3>
          {subStatus && (
            <div className="flex items-center gap-2">
              {isSubscribedAtUserLevel ? (
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full text-xs font-bold" data-testid="dashboard-premium-badge">
                  <Crown className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Premium · Unlimited practice
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full text-xs font-bold" data-testid="dashboard-free-badge">
                  Free plan · {subStatus.free_limit} per topic
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {practiceModules.map((module, index) => {
            const Icon = module.icon;
            const quota = getQuotaForModule(module.id);
            const isLocked = !isSubscribedAtUserLevel && quota && quota.remaining === 0;
            const percentUsed = quota ? (quota.used / quota.limit) * 100 : 0;
            const isLow = quota && !isSubscribedAtUserLevel && quota.remaining <= 10 && quota.remaining > 0;

            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ scale: 1.02, y: -4 }}
                className="bg-white rounded-3xl p-6 border-2 border-slate-200 shadow-sm cursor-pointer relative overflow-hidden"
                style={{ backgroundColor: module.color }}
                onClick={() => navigate(`/practice/${module.id}`)}
                data-testid={`module-card-${module.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-white/50 p-3 rounded-2xl">
                    <Icon className="w-6 h-6 text-slate-700" strokeWidth={2.5} />
                  </div>
                  {isLocked && (
                    <div className="bg-slate-800/80 text-white p-2 rounded-xl" data-testid={`module-locked-${module.id}`}>
                      <Lock className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                  )}
                </div>
                <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">{module.name}</h3>
                <p className="text-slate-600 text-sm mb-4">{module.description}</p>

                {/* Quota indicator */}
                {subStatus && (
                  <div data-testid={`module-quota-${module.id}`}>
                    {isSubscribedAtUserLevel ? (
                      <div className="inline-flex items-center gap-1.5 bg-white/70 text-amber-800 px-2.5 py-1 rounded-full text-xs font-bold">
                        <Crown className="w-3 h-3" strokeWidth={2.5} />
                        Unlimited
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between text-xs font-semibold text-slate-700 mb-1">
                          <span data-testid={`quota-text-${module.id}`}>
                            {isLocked ? (
                              <span className="text-red-700">Free limit reached</span>
                            ) : isLow ? (
                              <span className="text-amber-700">Only {quota.remaining} free left!</span>
                            ) : (
                              <>{quota.remaining} / {quota.limit} free left</>
                            )}
                          </span>
                          <span className="text-slate-500">{quota.used}/{quota.limit}</span>
                        </div>
                        <div className="w-full bg-white/60 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              isLocked
                                ? 'bg-red-500'
                                : isLow
                                ? 'bg-amber-500'
                                : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.min(100, percentUsed)}%` }}
                            data-testid={`quota-bar-${module.id}`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;